import React from "react";
import axios from "axios";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { formatPrice, formatPriceWithIva } from "./pricing";
import { ExpressWalletButtons } from "./ExpressWalletButtons";

// ─── Shipping destination → "Paese" pre-fill logic (shared by both selects) ──
// Zone labels must NEVER end up in the free-text country field: the backend
// validator rejects them (Polish customer blocked 8 times with "Europa").
const ZONE_NAMES = ["europa", "europe", "resto del mondo", "internazionale", "international", "america", "mondo", "world"];
const ZONE_LEFTOVERS = [...ZONE_NAMES, "nazionale", "national"];

const prefillNameForRule = (rule) => {
  if (!rule?.country) return "";
  let clean = rule.country.replace(/^[\p{Emoji_Presentation}\p{Extended_Pictographic}\s]+/u, "").trim() || rule.country;
  if (["nazionale", "national"].includes(clean.toLowerCase())) clean = "Italia";
  return ZONE_NAMES.includes(clean.toLowerCase()) ? "" : clean;
};

// Clean display label for a shipping rule: strips leading emoji from the DB
// country name (avoids "🇧🇷 🇧🇷 BRASILE" when icon+country both carry a flag)
// and converts ALL-CAPS names to Title Case.
export const ruleDisplayName = (rule) => {
  if (!rule?.country) return "";
  let clean = rule.country.replace(/^[\p{Emoji_Presentation}\p{Extended_Pictographic}\s]+/u, "").trim() || rule.country;
  if (clean === clean.toUpperCase() && /[A-Z]/.test(clean)) {
    clean = clean.toLowerCase().replace(/(^|\s|')\S/g, (m) => m.toUpperCase());
  }
  return clean;
};

export const applyDestinationChange = (prev, newRuleId, shippingRules) => {
  const newRule = shippingRules.find((r) => r.id === newRuleId);
  const prevRule = shippingRules.find((r) => r.id === prev.country);
  const prevCountry = (prev.specificCountry || "").trim();
  const prevPrefill = prefillNameForRule(prevRule);
  // Replace the field only if the customer never typed a custom value
  const wasAutoFilled =
    !prevCountry ||
    (prevPrefill && prevCountry.toLowerCase() === prevPrefill.toLowerCase()) ||
    ZONE_LEFTOVERS.includes(prevCountry.toLowerCase());
  const next = { ...prev, country: newRuleId, countryChosen: true };
  if (wasAutoFilled && newRule) next.specificCountry = prefillNameForRule(newRule);
  return next;
};

/* ---------- Single searchable country select (zone auto-derived) ---------- */
const _normSearch = (s) => (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

export const CountrySearchSelect = ({ countryMap, shippingRules, customerInfo, setCustomerInfo, idPrefix }) => {
  const [query, setQuery] = React.useState("");
  const q = _normSearch(query.trim());
  const filtered = q ? countryMap.filter((c) => _normSearch(c.name).includes(q)) : countryMap;
  const selectedName = customerInfo.specificCountry || "";
  const options = selectedName && !filtered.some((c) => c.name === selectedName)
    ? [countryMap.find((c) => c.name === selectedName) || { name: selectedName, rule_id: customerInfo.country }, ...filtered]
    : filtered;
  const currentRule = shippingRules.find((r) => r.id === customerInfo.country);
  const pick = (name) => {
    const entry = countryMap.find((c) => c.name === name);
    if (!entry) return;
    setQuery("");
    setCustomerInfo((prev) => ({ ...prev, specificCountry: entry.name, country: entry.rule_id, countryChosen: true }));
  };
  // Keep the shipping rule consistent with a pre-filled country name (e.g. a
  // logged-in French customer whose profile pre-fills "Francia" while the rule
  // is still the default "italia").
  React.useEffect(() => {
    if (!selectedName) return;
    const entry = countryMap.find((c) => c.name === selectedName);
    if (entry && entry.rule_id !== customerInfo.country) {
      setCustomerInfo((prev) => ({ ...prev, country: entry.rule_id, countryChosen: true }));
    }
  }, [selectedName, countryMap, customerInfo.country, setCustomerInfo]);
  return (
    <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3 my-3">
      <Label className="text-sm font-semibold text-gray-300 mb-2 block">🌍 Paese di spedizione / Shipping country *</Label>
      <Input
        type="text"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="🔍 Cerca la tua nazione... / Search your country..."
        className="mb-2 bg-[#0d0d14] border-gray-700 text-white placeholder-gray-500"
        data-testid={`${idPrefix}-country-search`}
      />
      {q ? (
        filtered.length > 0 ? (
          /* Live results while typing — click to select */
          <div className="bg-[#0d0d14] border border-gray-700 rounded-md max-h-52 overflow-y-auto divide-y divide-gray-800" data-testid={`${idPrefix}-country-results`}>
            {filtered.slice(0, 40).map((c) => (
              <button
                type="button"
                key={c.name}
                onClick={() => pick(c.name)}
                className="w-full text-left px-3 py-2 text-sm text-gray-200 hover:bg-blue-500/20 flex items-center gap-2"
                data-testid={`${idPrefix}-country-result-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <span>{c.flag || "🌍"}</span>
                <span>{c.name}</span>
              </button>
            ))}
          </div>
        ) : (
          <p className="text-xs text-red-400 mt-1" data-testid={`${idPrefix}-country-no-results`}>
            Nessuna nazione trovata / No country found
          </p>
        )
      ) : (
      <select
        data-testid={`${idPrefix}-country-select`}
        value={options.some((c) => c.name === selectedName) ? selectedName : ""}
        onChange={(e) => pick(e.target.value)}
        className="w-full px-3 py-2 border border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm bg-[#0d0d14] text-white"
        required
      >
        <option value="">— Seleziona la tua nazione / Select your country ({filtered.length}) —</option>
        {options.map((c) => (
          <option key={c.name} value={c.name}>{c.flag ? `${c.flag} ` : ""}{c.name}</option>
        ))}
      </select>
      )}
      {selectedName && currentRule && (
        <div className="text-xs text-gray-300 mt-2 space-y-0.5" data-testid={`${idPrefix}-zone-info`}>
          <div>📦 Tariffa: {currentRule.icon} {ruleDisplayName(currentRule)} — {currentRule.description}</div>
          <div>⏱️ Tempi di consegna: {currentRule.delivery_days}</div>
        </div>
      )}
    </div>
  );
};

/* ---------- Cart item row ---------- */
const CartItemRow = ({ item, onQuantityChange, onRemove }) => {
  const article = item?.article;
  if (!article || !Number.isFinite(Number(article.price))) return null;
  return (
  <div className="flex items-center justify-between border-b border-gray-800 pb-4">
    <div className="flex-1">
      <h3 className="font-medium text-white">{article.title || "Articolo"}</h3>
      <div className="text-sm text-gray-400 space-y-1">
        <div>
          Prezzo: {formatPrice(article.price)} + IVA {article.iva_percentage ?? 0}% ={" "}
          {formatPriceWithIva(article.price, article.iva_percentage ?? 0)}
        </div>
      </div>
    </div>
    <div className="flex items-center space-x-2">
      <Button variant="outline" size="sm" className="bg-transparent border-gray-700 text-gray-300 hover:bg-[#1c1c26] hover:text-white" onClick={() => onQuantityChange(item.article_id, item.quantity - 1)} data-testid={`cart-decrease-${item.article_id}`}>-</Button>
      <span className="w-8 text-center text-white">{item.quantity}</span>
      <Button variant="outline" size="sm" className="bg-transparent border-gray-700 text-gray-300 hover:bg-[#1c1c26] hover:text-white" onClick={() => onQuantityChange(item.article_id, item.quantity + 1)} data-testid={`cart-increase-${item.article_id}`}>+</Button>
      <Button variant="destructive" size="sm" onClick={() => onRemove(item.article_id)} data-testid={`cart-remove-${item.article_id}`}>🗑️</Button>
    </div>
  </div>
  );
};

/* ---------- Coupon section ---------- */
const CouponSection = ({ appliedCoupon, couponCode, setCouponCode, setCouponError, couponError, couponLoading, applyCoupon, removeCoupon, getDiscountAmount, customerInfo, setCustomerInfo }) => (
  <div className="border-t border-gray-800 pt-3 mt-2" data-testid="coupon-section">
    {!appliedCoupon ? (
      <div>
        <label className="text-xs font-medium text-gray-400 mb-1 block">Codice Sconto</label>
        <div className="flex gap-2">
          <input
            type="text"
            value={couponCode}
            onChange={(e) => { setCouponCode(e.target.value.toUpperCase()); setCouponError(""); }}
            placeholder="Es: PRIMAVERA5"
            className="flex-1 min-w-0 px-3 py-1.5 bg-[#0d0d14] border border-gray-700 text-white placeholder-gray-500 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
            data-testid="coupon-input"
          />
          <Button onClick={applyCoupon} disabled={couponLoading || !couponCode.trim()} className="bg-amber-500 hover:bg-amber-600 text-white text-sm px-3 py-1.5" data-testid="apply-coupon-btn">
            {couponLoading ? "..." : "Applica"}
          </Button>
        </div>
        {couponCode.trim() && <label className="block mt-2 text-xs text-gray-300" htmlFor="coupon-customer-email">Email associata al codice (per coupon personali)
          <Input id="coupon-customer-email" data-testid="coupon-customer-email" type="email" autoComplete="email" value={customerInfo.email || ""} onChange={(event) => { setCustomerInfo((previous) => ({ ...previous, email: event.target.value })); setCouponError(""); }} className="mt-1 bg-[#0d0d14] border-gray-700 text-white" placeholder="Email / E-mail" />
        </label>}
        {couponError && <p className="text-xs text-red-400 mt-1" role="alert" data-testid="coupon-error">{couponError}</p>}
      </div>
    ) : (
      <div className="bg-green-500/10 border border-green-500/30 rounded-md p-2" data-testid="coupon-applied">
        <div className="flex justify-between items-center">
          <div>
            <span className="text-sm font-medium text-green-300">Codice <span className="font-mono font-bold">{appliedCoupon.code}</span> applicato!</span>
            <p className="text-xs text-green-400">Sconto: -{formatPrice(getDiscountAmount())}</p>
          </div>
          <button onClick={removeCoupon} className="text-red-500 hover:text-red-700 text-xs font-medium" data-testid="remove-coupon-btn">Rimuovi</button>
        </div>
      </div>
    )}
  </div>
);

/* ---------- Payment method picker ---------- */
const PaymentMethodPicker = ({ onSelect, onBack, customerInfo, shippingRules = [] }) => {
  const rule = shippingRules.find((r) => r.id === customerInfo?.country);
  const rid = (customerInfo?.country || "").toLowerCase();
  const zone = (rule?.zone || "").toLowerCase();
  const rname = (rule?.country || "").toLowerCase();
  const isItaly = rid === "italia" || ["italia", "italy", "nazionale", "national"].includes(zone) || /ital|nazionale/.test(rname);
  const klarnaOk = isItaly
    || ["eu", "europea", "europe", "europa", "ch", "extra-ue", "cyprus", "czech", "norway"].includes(zone)
    || ["europa", "regno_unito", "uk-shipping", "svizzera", "norvegia", "cipro", "repubblica_ceca", "repubblica-ceca", "paese-bassi", "paesi-bassi"].includes(rid);
  const methods = [
    { id: "card", icon: "💳", title: "Carta di Credito", desc: "Carta · 🍎 Apple Pay · 🤖 Google Pay · Link" },
    // PayPal nascosto temporaneamente (PAYEE_ACCOUNT_RESTRICTED lato PayPal): riattivare dopo la verifica dell'account.
    { id: "transfer", icon: "🏦", title: "Bonifico Bancario", desc: "2 IBAN disponibili - Riceverai istruzioni" },
    ...(klarnaOk ? [{ id: "klarna", icon: "🔷", title: "Klarna", desc: "Paga dopo 30 giorni o in 3 rate" }] : []),
    ...(isItaly ? [{ id: "satispay", icon: "🟥", title: "Satispay", desc: "Pagamento mobile veloce e sicuro" }] : []),
  ];
  return (
  <div className="space-y-4">
    <h3 className="text-lg font-semibold text-center">Scegli modalità di pagamento</h3>
    <div className="grid grid-cols-1 gap-3">
      {methods.map((m) => (
        <Button key={m.id} variant="outline" onClick={() => onSelect(m.id)} data-testid={`payment-method-${m.id}`} className="flex items-center justify-center space-x-2 p-4 h-auto bg-transparent border-gray-700 text-gray-200 hover:bg-[#1c1c26] hover:border-pink-500/50 hover:text-white">
          <div className="text-2xl">{m.icon}</div>
          <div className="text-left">
            <div className="font-semibold">{m.title}</div>
            <div className="text-sm text-gray-400">{m.desc}</div>
          </div>
        </Button>
      ))}
    </div>
    {!isItaly && (
      <p className="text-xs text-gray-400 text-center" data-testid="payment-methods-foreign-hint">
        Satispay{klarnaOk ? "" : " e Klarna"} {klarnaOk ? "è disponibile" : "sono disponibili"} solo per {klarnaOk ? "l'Italia" : "Italia/UE"}. Se la carta viene rifiutata, puoi pagare con bonifico bancario.
      </p>
    )}
    <Button variant="outline" onClick={onBack} className="w-full bg-transparent border-gray-700 text-gray-300 hover:bg-[#1c1c26] hover:text-white">← Torna al Carrello</Button>
  </div>
  );
};

/* ---------- Shipping mismatch warning ----------
   Visual hint shown when the customer typed something that visibly conflicts
   with the selected shipping zone. The HARD BLOCK is enforced by
   `hasShippingMismatch` from ProdottiPage (passed to PaymentSubmitButton);
   this component is purely a UX hint. */
const ShippingMismatchWarning = ({ customerInfo, shippingRules, hasShippingMismatch }) => {
  const currentRule = shippingRules.find((r) => r.id === customerInfo.country);
  const currentZone = (currentRule?.zone || "").toLowerCase();
  const country = (customerInfo.specificCountry || "").trim().toLowerCase();
  // Accept both "national" (new) and "nazionale" (legacy IT schema)
  const isNationalZone = currentZone === "national" || currentZone === "nazionale" || currentRule?.id === "italia";

  // Use the canonical mismatch detector to decide if a warning is needed at all.
  // If hasShippingMismatch says "no mismatch" we don't show anything (avoids
  // false positives like the one that broke production for Calvizzano etc).
  if (typeof hasShippingMismatch === "function" && !hasShippingMismatch()) {
    // Special case: zone is foreign but the user typed something Italian-looking
    // → show a soft blue hint suggesting NAZIONALE. (Soft hint, no block.)
    const italianTerms = ["italia", "italy", "italian", "italiana", "nazionale", "national"];
    const looksItalian = country && italianTerms.some(t => country.includes(t));
    if (!isNationalZone && looksItalian) {
      return (
        <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3 text-sm" data-testid="shipping-national-hint">
          <p className="text-blue-300">Se ti trovi in Italia, seleziona "NAZIONALE" come destinazione per la tariffa corretta.</p>
        </div>
      );
    }
    return null;
  }

  // hasShippingMismatch() === true → render the explanatory warning
  if (isNationalZone) {
    return (
      <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-sm" data-testid="shipping-mismatch-warning">
        <p className="font-semibold text-amber-300">
          Attenzione: hai selezionato spedizione NAZIONALE ma il tuo paese risulta "{customerInfo.specificCountry}".
        </p>
        <p className="text-amber-400 mt-1">
          Se non ti trovi in Italia, seleziona la destinazione corretta (es. EUROPA) nel campo sopra per calcolare correttamente le spese di spedizione.
        </p>
      </div>
    );
  }
  return (
    <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3 text-sm" data-testid="shipping-national-hint">
      <p className="text-blue-300">Se ti trovi in Italia, seleziona "NAZIONALE" come destinazione per la tariffa corretta.</p>
    </div>
  );
};

/* ---------- EU customs reassurance notice (checkout) ----------
   Shown as soon as a foreign country is selected: EU customers see a green
   "no customs" reassurance (we ship from Italy), extra-EU customers see an
   informative note about possible local duties. Hidden for Italy. */
const EU_COUNTRIES = [
  "austria", "belgio", "belgium", "bulgaria", "cipro", "cyprus", "croazia", "croatia",
  "danimarca", "denmark", "estonia", "finlandia", "finland", "francia", "france",
  "germania", "germany", "grecia", "greece", "irlanda", "ireland", "lettonia", "latvia",
  "lituania", "lithuania", "lussemburgo", "luxembourg", "malta", "paesi bassi", "olanda",
  "netherlands", "polonia", "poland", "portogallo", "portugal", "repubblica ceca",
  "czech republic", "czechia", "romania", "slovacchia", "slovakia", "slovenia",
  "spagna", "spain", "svezia", "sweden", "ungheria", "hungary",
];

const EuCustomsCheckoutNotice = ({ customerInfo, shippingRules }) => {
  const name = (customerInfo.specificCountry || "").trim().toLowerCase();
  if (!name) return null;
  const rule = shippingRules.find((r) => r.id === customerInfo.country);
  const zone = (rule?.zone || "").toLowerCase();
  const isItaly = name === "italia" || name === "italy" || zone === "national" || zone === "nazionale" || rule?.id === "italia";
  if (isItaly) return null;
  const norm = name.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  const isEu = EU_COUNTRIES.some((c) => norm === c || norm.startsWith(c));

  if (isEu) {
    return (
      <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3 flex items-start gap-2.5" data-testid="eu-customs-checkout-notice">
        <span className="text-xl leading-none mt-0.5">🇪🇺</span>
        <div className="text-sm">
          <p className="font-bold text-green-300">Nessun dazio doganale per te!</p>
          <p className="text-green-400 text-xs mt-0.5">
            Spediamo dall'Italia 🇮🇹: all'interno dell'UE <strong>non paghi dogana né costi extra</strong> alla consegna. Il totale che vedi è quello finale.
          </p>
        </div>
      </div>
    );
  }
  return (
    <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3 flex items-start gap-2.5" data-testid="extra-eu-customs-checkout-notice">
      <span className="text-xl leading-none mt-0.5">📦</span>
      <div className="text-sm">
        <p className="font-bold text-blue-300">Spedizione extra-UE ({customerInfo.specificCountry})</p>
        <p className="text-blue-400 text-xs mt-0.5">
          Spediamo dall'Italia con tracking assicurato. Eventuali dazi o tasse locali alla consegna dipendono dal tuo paese e sono a carico del destinatario.
        </p>
      </div>
    </div>
  );
};

/* ---------- Payment submit button (per method) ---------- */
const PaymentSubmitButton = ({ method, customerInfo, hasShippingMismatch, isCheckingOut, setIsCheckingOut, handleOrderSubmit, processStripeCheckout, processPayPalCheckout }) => {
  const incomplete =
    !customerInfo.name || !customerInfo.email || !customerInfo.phone ||
    !customerInfo.address || !customerInfo.city || !customerInfo.zipCode;
  const mismatch = hasShippingMismatch();

  if (incomplete) {
    return (
      <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 mb-4">
        <p className="text-sm text-yellow-300 text-center">Compila tutti i campi obbligatori prima di procedere</p>
      </div>
    );
  }
  if (mismatch) {
    return (
      <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-4" data-testid={`shipping-block-${method}`}>
        <p className="text-sm text-red-300 text-center font-semibold">
          La destinazione di spedizione non corrisponde al paese inserito. Correggi prima di procedere.
        </p>
      </div>
    );
  }
  if (method === "satispay") {
    return (
      <Button onClick={handleOrderSubmit} disabled={isCheckingOut} className="flex-1 bg-red-600 hover:bg-red-700 text-white font-semibold py-6 text-lg">
        {isCheckingOut ? "Caricamento..." : "🟥 Conferma e Paga con Satispay"}
      </Button>
    );
  }
  if (method === "card" || method === "klarna") {
    return (
      <Button onClick={processStripeCheckout} disabled={isCheckingOut} className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-6 text-lg">
        {isCheckingOut ? "Caricamento..." : method === "klarna" ? "🔷 Paga con Klarna" : "💳 Paga con Carta"}
      </Button>
    );
  }
  if (method === "paypal") {
    return (
      <Button onClick={processPayPalCheckout} disabled={isCheckingOut} className="flex-1 bg-[#0070ba] hover:bg-[#005ea6] text-white font-semibold py-6 text-lg" data-testid="paypal-submit-btn">
        {isCheckingOut ? "Caricamento..." : "🅿️ Paga con PayPal"}
      </Button>
    );
  }
  return (
    <Button onClick={handleOrderSubmit} disabled={isCheckingOut || !customerInfo.specificCountry?.trim()} className="flex-1">
      {isCheckingOut ? "Invio in corso..." : "Conferma Ordine"}
    </Button>
  );
};

/* ---------- Customer info form ---------- */
const FORM_FIELDS = [
  { id: "name", label: "Nome completo *", placeholder: "Mario Rossi" },
  { id: "email", label: "Email *", placeholder: "mario@email.com", type: "email" },
  { id: "phone", label: "Telefono *", placeholder: "+39 123 456 7890" },
  { id: "address", label: "Indirizzo di spedizione *", placeholder: "Via Roma 123" },
  { id: "city", label: "Città *", placeholder: "Roma" },
  { id: "zipCode", label: "CAP", placeholder: "00100" },
];

const CustomerInfoForm = ({ customerInfo, setCustomerInfo, shippingRules, shippingRulesNoInpost, createAccount, setCreateAccount, accountPassword, setAccountPassword, hasShippingMismatch, countryMap }) => {
  // Country dropdown data per shipping zone (server-provided, always valid
  // for the backend validator — replaces the free-text field that let
  // customers submit zone names or typos)
  const [zoneCountries, setZoneCountries] = React.useState(null);
  const [otherMode, setOtherMode] = React.useState(false);
  React.useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/api/shipping-zones/countries`)
      .then((r) => setZoneCountries(r.data || []))
      .catch(() => setZoneCountries([]));
  }, []);
  React.useEffect(() => setOtherMode(false), [customerInfo.country]);
  const zoneInfo = (zoneCountries || []).find((z) => z.rule_id === customerInfo.country);
  const countryList = zoneInfo?.countries || [];
  const allowOther = !!zoneInfo?.allow_other;
  const isListed = countryList.includes(customerInfo.specificCountry);
  return (
  <div className="space-y-3 max-h-60 overflow-y-auto">
    {FORM_FIELDS.map((f) => (
      <div key={f.id}>
        <Label htmlFor={f.id}>{f.label}</Label>
        <Input
          id={f.id}
          type={f.type || "text"}
          value={customerInfo[f.id]}
          onChange={(e) => setCustomerInfo((prev) => ({ ...prev, [f.id]: e.target.value }))}
          placeholder={f.placeholder}
          required={f.label.endsWith("*")}
          className="bg-[#0d0d14] border-gray-700 text-white placeholder-gray-500"
        />
      </div>
    ))}

    {countryMap && countryMap.length > 0 ? (
      /* Single searchable country select — zone & rate auto-derived */
      <CountrySearchSelect
        countryMap={countryMap}
        shippingRules={shippingRules}
        customerInfo={customerInfo}
        setCustomerInfo={setCustomerInfo}
        idPrefix="checkout"
      />
    ) : (
    <>
    <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
      <Label htmlFor="checkout-country" className="text-sm font-semibold text-gray-300 mb-2 block">Destinazione spedizione *</Label>
      <select
        id="checkout-country"
        data-testid="checkout-country-select"
        value={customerInfo.country}
        onChange={(e) => {
          const newRuleId = e.target.value;
          setCustomerInfo((prev) => applyDestinationChange(prev, newRuleId, shippingRules));
        }}
        className="w-full px-3 py-2 bg-[#0d0d14] border border-gray-700 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
      >
        {shippingRulesNoInpost.map((rule) => (
          <option key={rule.id} value={rule.id}>{rule.icon} {rule.country} - {rule.description}</option>
        ))}
      </select>
    </div>

    <div>
      <Label htmlFor="specificCountry">Paese / Country *</Label>
      {countryList.length > 0 && !(otherMode && allowOther) ? (
        <>
          <select
            id="specificCountry"
            data-testid="specific-country-select"
            value={isListed ? customerInfo.specificCountry : ""}
            onChange={(e) => {
              if (e.target.value === "__other__") {
                setOtherMode(true);
                setCustomerInfo((prev) => ({ ...prev, specificCountry: "" }));
              } else {
                setCustomerInfo((prev) => ({ ...prev, specificCountry: e.target.value, countryChosen: true }));
              }
            }}
            className="w-full px-3 py-2 border border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm bg-[#0d0d14] text-white"
            required
          >
            <option value="">— Seleziona il tuo paese / Select your country —</option>
            {countryList.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
            {allowOther && <option value="__other__">Altro… / Other…</option>}
          </select>
        </>
      ) : (
        <>
          <Input
            id="specificCountry"
            data-testid="specific-country-input"
            value={customerInfo.specificCountry}
            onChange={(e) => setCustomerInfo((prev) => ({ ...prev, specificCountry: e.target.value }))}
            placeholder="Es: Emirati Arabi, Serbia..."
            required
            className="bg-[#0d0d14] border-gray-700 text-white placeholder-gray-500"
          />
          {allowOther && otherMode && (
            <button
              type="button"
              onClick={() => setOtherMode(false)}
              className="text-xs text-blue-400 hover:underline mt-1"
              data-testid="back-to-country-list"
            >
              ← Torna all'elenco paesi
            </button>
          )}
        </>
      )}
    </div>
    </>
    )}

    <EuCustomsCheckoutNotice customerInfo={customerInfo} shippingRules={shippingRules} />

    <ShippingMismatchWarning customerInfo={customerInfo} shippingRules={shippingRules} hasShippingMismatch={hasShippingMismatch} />

    <div className="col-span-2 border-t border-gray-800 pt-4 mt-2">
      <div className="flex items-center space-x-2 mb-3">
        <input
          type="checkbox"
          id="createAccount"
          checked={createAccount}
          onChange={(e) => setCreateAccount(e.target.checked)}
          className="w-4 h-4 text-blue-600 border-gray-600 rounded focus:ring-blue-500"
        />
        <Label htmlFor="createAccount" className="cursor-pointer font-normal">✨ Crea un account per salvare i tuoi dati e vedere lo storico ordini</Label>
      </div>
      {createAccount && (
        <div>
          <Label htmlFor="accountPassword">Password per il tuo account *</Label>
          <Input
            id="accountPassword"
            type="password"
            value={accountPassword}
            onChange={(e) => setAccountPassword(e.target.value)}
            placeholder="Scegli una password sicura"
            required={createAccount}
            minLength={6}
            className="bg-[#0d0d14] border-gray-700 text-white placeholder-gray-500"
          />
          <p className="text-xs text-gray-500 mt-1">Minimo 6 caratteri</p>
        </div>
      )}
    </div>
  </div>
  );
};

/* ---------- Main CartModal ---------- */
export const CartModal = ({
  isOpen, onClose,
  cart, getCartItemsCount, updateCartQuantity, removeFromCart,
  getCartItemsTotal, getCartTotal, getShippingTotal,
  shippingRules, shippingRulesNoInpost, customerInfo, setCustomerInfo,
  appliedCoupon, couponCode, setCouponCode, couponError, setCouponError, couponLoading, applyCoupon, removeCoupon, getDiscountAmount,
  showPaymentOptions, setShowPaymentOptions, selectedPaymentMethod, setSelectedPaymentMethod,
  handleCheckout, handlePaymentSelection, handleOrderSubmit, processStripeCheckout, processPayPalCheckout,
  buildExpressPayload,
  isCheckingOut, setIsCheckingOut, hasShippingMismatch,
  createAccount, setCreateAccount, accountPassword, setAccountPassword,
  resetCheckoutState,
}) => {
  // Country map for the searchable nation dropdown (fetched once per open, with retries:
  // if it fails we'd fall back to the legacy zone select with Italy preselected — the
  // case where a French customer ended up paying the Italian rate).
  const [countryMap, setCountryMap] = React.useState(null);
  React.useEffect(() => {
    if (!isOpen || countryMap !== null) return;
    let cancelled = false;
    const load = async (attempt = 0) => {
      try {
        const r = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/shipping-zones/country-map`, { timeout: 8000 });
        if (!cancelled) setCountryMap(r.data?.countries || []);
      } catch (e) {
        if (cancelled) return;
        if (attempt < 3) setTimeout(() => load(attempt + 1), 1500 * (attempt + 1));
        else setCountryMap([]);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [isOpen, countryMap]);

  const countryRequired = Array.isArray(countryMap) && countryMap.length > 0;
  const guardedCheckout = () => {
    if (countryRequired && !(customerInfo.specificCountry || "").trim()) {
      alert("🌍 Seleziona il paese di spedizione prima di procedere.\nPlease select your shipping country before proceeding.");
      return;
    }
    handleCheckout();
  };

  if (!isOpen) return null;

  const handleClose = () => { onClose(); resetCheckoutState(); };
  const methodLabel = {
    card: "💳 Carta di Credito",
    satispay: "🟥 Satispay",
    transfer: "🏦 Bonifico",
    klarna: "🔷 Klarna",
  }[selectedPaymentMethod] || "Seleziona un metodo";

  const currentRule = shippingRules.find((r) => r.id === customerInfo.country);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-[#12121a] border border-gray-800 text-gray-200 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6" style={{ maxHeight: "80vh", overflowY: "auto" }}>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold text-white">Carrello ({getCartItemsCount()} articoli)</h2>
            <Button variant="outline" onClick={handleClose} className="bg-transparent border-gray-700 text-gray-300 hover:bg-[#1c1c26] hover:text-white">✕</Button>
          </div>

          {cart.length === 0 ? (
            <p className="text-gray-400">Il carrello è vuoto</p>
          ) : (
            <>
              <div className="space-y-4 mb-6">
                {cart.map((item) => (
                  <CartItemRow key={item.article_id} item={item} onQuantityChange={updateCartQuantity} onRemove={removeFromCart} />
                ))}
              </div>

              <div className="border-t border-gray-800 pt-4">
                <div className="space-y-2 mb-4">
                  <div className="flex justify-between text-sm">
                    <span>Subtotale prodotti:</span>
                    <span>{formatPrice(getCartItemsTotal())}</span>
                  </div>

                  {shippingRules.length > 0 && (countryMap && countryMap.length > 0 ? (
                    <CountrySearchSelect
                      countryMap={countryMap}
                      shippingRules={shippingRules}
                      customerInfo={customerInfo}
                      setCustomerInfo={setCustomerInfo}
                      idPrefix="cart"
                    />
                  ) : (
                    <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3 my-3">
                      <Label htmlFor="cart-country" className="text-sm font-semibold text-gray-300 mb-2 block">🌍 Seleziona destinazione spedizione:</Label>
                      <select
                        id="cart-country"
                        value={customerInfo.country}
                        onChange={(e) => setCustomerInfo((prev) => applyDestinationChange(prev, e.target.value, shippingRules))}
                        className="w-full px-3 py-2 bg-[#0d0d14] border border-gray-700 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                      >
                        {shippingRulesNoInpost.map((rule) => (
                          <option key={rule.id} value={rule.id}>{rule.icon} {rule.country} - {rule.description}</option>
                        ))}
                      </select>
                      <div className="text-xs text-gray-400 mt-2">⏱️ Tempi di consegna: {currentRule?.delivery_days}</div>
                    </div>
                  ))}

                  {shippingRules.length > 0 && (
                    <div className="text-sm">
                      {customerInfo.specificCountry || !countryRequired ? (
                        <div className="flex justify-between">
                          <span>Spedizione {currentRule?.icon} {ruleDisplayName(currentRule)}:</span>
                          <span className="font-semibold text-blue-400">{formatPrice(getShippingTotal())}</span>
                        </div>
                      ) : (
                        <div className="flex justify-between text-amber-400" data-testid="cart-shipping-select-country">
                          <span>Spedizione:</span>
                          <span className="font-semibold">⬆️ seleziona il paese / select country</span>
                        </div>
                      )}
                      <div className="text-xs text-gray-500 mt-1">{getCartItemsCount()} articoli totali</div>
                    </div>
                  )}

                  <CouponSection
                    customerInfo={customerInfo}
                    setCustomerInfo={setCustomerInfo}
                    appliedCoupon={appliedCoupon}
                    couponCode={couponCode}
                    setCouponCode={setCouponCode}
                    setCouponError={setCouponError}
                    couponError={couponError}
                    couponLoading={couponLoading}
                    applyCoupon={applyCoupon}
                    removeCoupon={removeCoupon}
                    getDiscountAmount={getDiscountAmount}
                  />

                  {appliedCoupon && (
                    <div className="flex justify-between text-sm text-green-400">
                      <span>Sconto ({appliedCoupon.discount_type === "percentage" ? `${appliedCoupon.discount_value}%` : `€${appliedCoupon.discount_value}`}):</span>
                      <span className="font-semibold">-{formatPrice(getDiscountAmount())}</span>
                    </div>
                  )}

                  <div className="flex justify-between items-center font-bold text-lg border-t border-gray-800 pt-2">
                    <span className="text-white">Totale:</span>
                    <span className="text-green-400" data-testid="cart-total">
                      {countryRequired && !customerInfo.specificCountry ? `${formatPrice(getCartItemsTotal())} + spedizione` : formatPrice(getCartTotal())}
                    </span>
                  </div>
                </div>

                {!showPaymentOptions ? (
                  <>
                    {/* Express Checkout (Apple Pay / Google Pay / Link) — visible upfront */}
                    {buildExpressPayload && (
                      <ExpressWalletButtons
                        amount={getCartTotal()}
                        currency="eur"
                        getPayload={buildExpressPayload}
                        disabled={isCheckingOut}
                        onError={(msg) => alert("❌ " + msg)}
                      />
                    )}
                    <div className="flex flex-col sm:flex-row gap-3">
                      <Button data-testid="cart-continue-shopping" variant="outline" onClick={handleClose} className="flex-1 bg-transparent border-gray-700 text-gray-300 hover:bg-[#1c1c26] hover:text-white">Continua Shopping</Button>
                      <Button onClick={guardedCheckout} className="flex-1 bg-green-600 hover:bg-green-700" data-testid="cart-proceed-btn">Procedi al Pagamento</Button>
                    </div>
                  </>
                ) : !selectedPaymentMethod ? (
                  <PaymentMethodPicker onSelect={handlePaymentSelection} onBack={() => setShowPaymentOptions(false)} customerInfo={customerInfo} shippingRules={shippingRules} />
                ) : (
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-center text-white">Modalità: {methodLabel}</h3>
                    {couponError && !appliedCoupon && <p role="alert" data-testid="checkout-coupon-warning" className="text-sm text-amber-300 border-l-2 border-amber-400 pl-3">{couponError}</p>}
                    <CustomerInfoForm
                      customerInfo={customerInfo}
                      setCustomerInfo={setCustomerInfo}
                      shippingRules={shippingRules}
                      shippingRulesNoInpost={shippingRulesNoInpost}
                      createAccount={createAccount}
                      setCreateAccount={setCreateAccount}
                      accountPassword={accountPassword}
                      setAccountPassword={setAccountPassword}
                      hasShippingMismatch={hasShippingMismatch}
                      countryMap={countryMap}
                    />
                    {/* Apple Pay / Google Pay express button (only for "card" method) */}
                    {selectedPaymentMethod === "card" && buildExpressPayload && (
                      <ExpressWalletButtons
                        amount={getCartTotal()}
                        currency="eur"
                        getPayload={buildExpressPayload}
                        disabled={isCheckingOut}
                        onError={(msg) => alert("❌ " + msg)}
                      />
                    )}
                    <div className="flex flex-col sm:flex-row gap-3 mt-6 pt-4 border-t border-gray-800 sticky bottom-0 bg-[#12121a]">
                      <Button
                        data-testid="checkout-change-payment"
                        variant="outline"
                        onClick={() => {
                          setSelectedPaymentMethod("");
                        }}
                        className="flex-1 bg-transparent border-gray-700 text-gray-300 hover:bg-[#1c1c26] hover:text-white"
                        disabled={isCheckingOut}
                      >
                        ← Cambia Pagamento
                      </Button>
                      <div className="flex-1">
                        <PaymentSubmitButton
                          method={selectedPaymentMethod}
                          customerInfo={customerInfo}
                          hasShippingMismatch={hasShippingMismatch}
                          isCheckingOut={isCheckingOut}
                          setIsCheckingOut={setIsCheckingOut}
                          handleOrderSubmit={handleOrderSubmit}
                          processStripeCheckout={processStripeCheckout}
                          processPayPalCheckout={processPayPalCheckout}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
